<template>
  <Teleport to="body">
    <Transition name="action-sheet">
      <div v-if="modelValue" class="action-sheet-overlay" @click="close">
        <div class="action-sheet" @click.stop>
          <div v-if="title" class="action-sheet__header">
            <h3 class="action-sheet__title">{{ title }}</h3>
            <p v-if="subtitle" class="action-sheet__subtitle">{{ subtitle }}</p>
          </div>
          <div class="action-sheet__actions">
            <button
              v-for="action in actions"
              :key="action.value"
              class="action-sheet__action"
              :class="{
                'action-sheet__action--danger': action.danger,
                'action-sheet__action--disabled': action.disabled,
              }"
              :disabled="action.disabled"
              @click="handleAction(action)"
            >
              <BaseIcon v-if="action.icon" :name="action.icon" size="20" />
              <span>{{ action.label }}</span>
            </button>
          </div>
          <button class="action-sheet__cancel" @click="close">
            {{ cancelText }}
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
  import { defineComponent } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  export default defineComponent({
    name: 'ActionSheet',

    components: {
      BaseIcon,
    },

    props: {
      modelValue: {
        type: Boolean,
        default: false,
      },
      title: {
        type: String,
        default: '',
      },
      subtitle: {
        type: String,
        default: '',
      },
      actions: {
        type: Array,
        required: true,
        // Each action: { label: string, value: string, icon?: string, danger?: boolean, disabled?: boolean }
      },
      cancelText: {
        type: String,
        default: 'Annuleren',
      },
    },

    emits: ['update:modelValue', 'action'],

    setup(props, { emit }) {
      const close = () => {
        emit('update:modelValue', false);
      };

      const handleAction = (action) => {
        if (action.disabled) return;
        emit('action', action.value);
        close();
      };

      return {
        close,
        handleAction,
      };
    },
  });
</script>

<style scoped lang="scss">
  .action-sheet-overlay {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
    padding: 0.5rem;
  }

  .action-sheet {
    width: 100%;
    max-width: 400px;
    background: var(--white);
    border-radius: 1rem;
    overflow: hidden;
    margin-bottom: env(safe-area-inset-bottom, 0);

    &__header {
      padding: 1rem 1.25rem;
      text-align: center;
      border-bottom: 1px solid var(--slate-100);
    }

    &__title {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      color: var(--slate-900);
    }

    &__subtitle {
      margin: 0.25rem 0 0;
      font-size: 0.875rem;
      color: var(--slate-500);
    }

    &__actions {
      display: flex;
      flex-direction: column;
    }

    &__action {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.75rem;
      padding: 1rem 1.25rem;
      background: none;
      border: none;
      border-bottom: 1px solid var(--slate-100);
      font-size: 1rem;
      font-weight: 500;
      color: var(--slate-700);
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: var(--slate-50);
      }

      &:active {
        background-color: var(--slate-100);
      }

      &:last-child {
        border-bottom: none;
      }

      &--danger {
        color: var(--red-600);

        .icon {
          color: var(--red-600);
        }
      }

      &--disabled {
        opacity: 0.5;
        cursor: not-allowed;

        &:hover {
          background-color: transparent;
        }
      }

      .icon {
        color: var(--slate-500);
      }
    }

    &__cancel {
      display: block;
      width: 100%;
      padding: 1rem 1.25rem;
      margin-top: 0.5rem;
      background: var(--white);
      border: none;
      border-radius: 1rem;
      font-size: 1rem;
      font-weight: 600;
      color: var(--slate-500);
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: var(--slate-50);
      }

      &:active {
        background-color: var(--slate-100);
      }
    }
  }

  // Transition
  .action-sheet-enter-active,
  .action-sheet-leave-active {
    transition: opacity 0.2s ease;

    .action-sheet {
      transition: transform 0.25s ease;
    }
  }

  .action-sheet-enter-from,
  .action-sheet-leave-to {
    opacity: 0;

    .action-sheet {
      transform: translateY(100%);
    }
  }
</style>
